/**
 * Formats a date string (YYYY-MM-DD or ISO string) to DD-MM-YYYY format.
 * Example: '2024-11-19' -> '19-11-2024'
 */
export function formatDateDDMMYYYY(dateStr?: string | null): string {
  if (!dateStr) return '';

  // Matches YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    const [, yyyy, mm, dd] = ymdMatch;
    return `${dd}-${mm}-${yyyy}`;
  }

  // Already DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  // Fallback parsing via Date object
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  return dateStr;
}

/**
 * Detects the user's local Indonesian timezone abbreviation (WIB, WITA, WIT)
 * or falls back to GMT+/-X for non-Indonesian zones.
 */
export function getLocalTimezoneName(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (
      tz.includes('Jakarta') ||
      tz.includes('Pontianak') ||
      tz.includes('Bangkok') ||
      tz.includes('Asia/Jakarta')
    ) {
      return 'WIB';
    }
    if (
      tz.includes('Makassar') ||
      tz.includes('Ujung_Pandang') ||
      tz.includes('Bali') ||
      tz.includes('Singapore') ||
      tz.includes('Kuala_Lumpur') ||
      tz.includes('Asia/Makassar')
    ) {
      return 'WITA';
    }
    if (tz.includes('Jayapura') || tz.includes('Asia/Jayapura') || tz.includes('Tokyo')) {
      return 'WIT';
    }

    const offsetHours = -new Date().getTimezoneOffset() / 60;
    if (offsetHours === 8) return 'WITA';
    if (offsetHours === 9) return 'WIT';
    return 'WIB';
  } catch {
    return 'WIB';
  }
}

/**
 * Formats an event time string (e.g. ISO string "2026-09-20T18:00:00+08:00")
 * into local device time.
 * Example:
 * - If user is in Jakarta (WIB): "17.00 WIB"
 * - If user is in Makassar (WITA): "18.00 WITA"
 * - If user is in Jayapura (WIT): "19.00 WIT"
 */
export function formatLocalEventTime(timeStr?: string | null, withZone = true): string {
  if (!timeStr) return '';
  const tzName = getLocalTimezoneName();
  try {
    if (timeStr.includes('T') || timeStr.includes('Z') || (timeStr.includes('+') && timeStr.length > 5)) {
      const d = new Date(timeStr);
      if (!isNaN(d.getTime())) {
        const timeFormatted = d.toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).replace(':', '.');
        return withZone ? `${timeFormatted} ${tzName}` : timeFormatted;
      }
    }
    // Fallback for simple "HH:MM"
    const cleanTime = timeStr.replace(/ (WIB|WITA|WIT)/gi, '').replace(':', '.').trim();
    return withZone ? `${cleanTime} ${tzName}` : cleanTime;
  } catch {
    return timeStr;
  }
}

/**
 * Formats a start and end time into a single clean range.
 * Example: "17.00 - 19.00 WIB" or "18.00 - 20.00 WITA"
 */
export function formatLocalEventTimeRange(startStr?: string | null, endStr?: string | null): string {
  const start = formatLocalEventTime(startStr, !endStr);
  const end = formatLocalEventTime(endStr, true);
  if (!start && !end) return '';
  if (!end) return start;
  if (!start) return end;
  return `${start} - ${end}`;
}
